/**
 * Shared sample avatars for blocks that seed a `brandy/avatar` on insertion.
 *
 * Seeded as image URLs rather than media-library ids: a fresh install has no
 * matching attachments, so an id would resolve to nothing and the avatar would
 * fall back to an initials tile — the empty-looking state these templates exist
 * to avoid. The URLs point at the project's own image host, the same source the
 * shipped patterns already use (see themes/brandy/patterns/team-members-2.php,
 * which renders these very files at 190x190).
 *
 * Names and images are paired, so the tooltip and the face belong together.
 * Consumers take the first N for a template and cycle the list when appending.
 */

import { __ } from '@wordpress/i18n';

const IMAGE_BASE =
	'https://images.wpbrandy.com/uploads/fashion-team-avatar-compress-image-';

/**
 * Sample people, in insertion order.
 *
 * The first five match `example` in avatar-group/block.json and team-member's
 * own template, so the inserter preview and the block an author actually gets
 * stay the same thing.
 *
 * @type {Array<{name: string, url: string}>}
 */
export const DEFAULT_AVATARS = [
	{ name: __( 'Ava Bennett', 'brandy-blocks' ), url: `${ IMAGE_BASE }1.webp` },
	{ name: __( 'Liam Carter', 'brandy-blocks' ), url: `${ IMAGE_BASE }2.webp` },
	{ name: __( 'Maya Diaz', 'brandy-blocks' ), url: `${ IMAGE_BASE }3.webp` },
	{ name: __( 'Noah Evans', 'brandy-blocks' ), url: `${ IMAGE_BASE }4.webp` },
	{ name: __( 'Sofia Khan', 'brandy-blocks' ), url: `${ IMAGE_BASE }5.webp` },
	{ name: __( 'Ethan Brooks', 'brandy-blocks' ), url: `${ IMAGE_BASE }6.webp` },
	{ name: __( 'Priya Raman', 'brandy-blocks' ), url: `${ IMAGE_BASE }7.webp` },
	{ name: __( 'Jonas Weber', 'brandy-blocks' ), url: `${ IMAGE_BASE }8.webp` },
	{ name: __( 'Chloe Martin', 'brandy-blocks' ), url: `${ IMAGE_BASE }9.webp` },
];

/**
 * Attributes for the nth sample avatar, wrapping around the list.
 *
 * `mediaId` stays 0 on purpose: these images live on a remote host and have no
 * attachment row, which is exactly how an avatar inserted by URL is stored.
 *
 * @param {number} index Zero-based position; values past the end wrap.
 * @return {{mediaId: number, url: string, name: string, alt: string}} Attributes.
 */
export function getDefaultAvatarAttributes( index ) {
	const { name, url } = DEFAULT_AVATARS[ index % DEFAULT_AVATARS.length ];
	return { mediaId: 0, url, name, alt: name };
}
